import { Box, CircularProgress, Container } from "@mui/material";
import { applyActionCode, checkActionCode } from "firebase/auth";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Text from "../../components/ui/Text";
import {
  EMAIL_FOR_SIGN_IN_KEY,
  POST_SIGNIN_REDIRECT_KEY,
} from "../../constants/auth";
import { ROUTES } from "../../constants/routes";
import { useAuth } from "../../contexts/AuthContext";
import { auth } from "../../firebase/config";

const AuthAction = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { completeSignInWithLink } = useAuth();
  const [status, setStatus] = useState("loading"); // loading, success, error, needsEmail
  const [message, setMessage] = useState("");

  const [confirmEmail, setConfirmEmail] = useState("");
  const [confirmEmailError, setConfirmEmailError] = useState("");
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  const mode = searchParams.get("mode");
  const oobCode = searchParams.get("oobCode");

  useEffect(() => {
    const handleAuthAction = async () => {
      if (!mode || !oobCode) {
        setStatus("error");
        setMessage("Invalid or missing action parameters.");
        return;
      }

      try {
        switch (mode) {
          case "verifyEmail":
            await handleVerifyEmail(oobCode);
            break;
          case "resetPassword":
            // Store the code and redirect to password reset page
            navigate(`${ROUTES.CREATE_NEW_PASSWORD}?oobCode=${oobCode}`, {
              replace: true,
            });
            break;
          case "recoverEmail":
            await handleRecoverEmail(oobCode);
            break;
          case "signIn":
            await handleSignInWithEmailLink();
            break;
          default:
            setStatus("error");
            setMessage("Unknown action mode.");
        }
      } catch (error) {
        setStatus("error");
        setMessage(
          error.message ||
            "An error occurred while processing your request. The link may have expired.",
        );
      }
    };

    handleAuthAction();
  }, [mode, oobCode, navigate]);

  const handleVerifyEmail = async (code) => {
    try {
      // First check if the code is valid
      await checkActionCode(auth, code);

      // Apply the email verification
      await applyActionCode(auth, code);

      // Redirect to success page
      navigate(ROUTES.EMAIL_CONFIRMED, { replace: true });
    } catch (error) {
      throw new Error(
        "Failed to verify email. The link may have expired or already been used.",
      );
    }
  };

  const handleRecoverEmail = async (code) => {
    try {
      await checkActionCode(auth, code);
      await applyActionCode(auth, code);
      setStatus("success");
      setMessage("Your email has been recovered successfully.");
    } catch (error) {
      throw new Error("Failed to recover email. The link may have expired.");
    }
  };

  // Complete sign-in from the emailed magic link and redirect
  const finishSignIn = async (email, url) => {
    try {
      const userCredential = await completeSignInWithLink(email, url);
      const redirectTarget = window.localStorage.getItem(
        POST_SIGNIN_REDIRECT_KEY,
      );
      window.localStorage.removeItem(POST_SIGNIN_REDIRECT_KEY);

      if (redirectTarget) {
        navigate(redirectTarget, {
          replace: true,
          state: {
            from: ROUTES.AUTH_ACTION,
            currentUserId: userCredential.user.uid,
          },
        });
      } else {
        // PublicRoute (wrapping /signin) will redirect to /account-setup or
        // /dashboard based on currentUser, same as it already does for
        // Google sign-in - AuthAction itself isn't wrapped in PublicRoute.
        navigate(ROUTES.SIGNIN, { replace: true });
      }
    } catch (error) {
      throw new Error(
        error.friendlyMessage ||
          "Failed to sign in. The link may have expired or already been used.",
      );
    }
  };

  const handleSignInWithEmailLink = async () => {
    const url = window.location.href;
    const storedEmail = window.localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);

    if (!storedEmail) {
      // Link was opened on a different device/browser than it was requested
      // from - ask the user to confirm their email to complete sign-in.
      setStatus("needsEmail");
      return;
    }

    await finishSignIn(storedEmail, url);
  };

  const handleConfirmEmailSubmit = async (e) => {
    e.preventDefault();

    if (!confirmEmail.trim()) {
      setConfirmEmailError("Please enter your email");
      return;
    }

    try {
      setConfirmSubmitting(true);
      setConfirmEmailError("");
      await finishSignIn(confirmEmail.trim(), window.location.href);
    } catch (error) {
      setConfirmEmailError(
        error.message || "Failed to sign in with this email.",
      );
    } finally {
      setConfirmSubmitting(false);
    }
  };

  if (status === "loading") {
    return (
      <Container>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <CircularProgress size={48} sx={{ mb: 3 }} />
          <Text variant="pageHeader" sx={{ mb: 2 }}>
            Processing...
          </Text>
          <Text variant="paragraph" sx={{ color: "#666666" }}>
            Please wait while we verify your request.
          </Text>
        </Box>
      </Container>
    );
  }

  if (status === "needsEmail") {
    return (
      <Container>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            mx: "auto",
          }}
        >
          <Text variant="pageHeader" sx={{ mb: 2 }}>
            Confirm your email
          </Text>
          <Text
            variant="paragraph"
            sx={{ color: "#666666", mb: 3, maxWidth: "400px" }}
          >
            This sign-in link was opened on a different device or browser.
            Please re-enter your email to confirm and continue.
          </Text>

          <Box sx={{ width: "100%", maxWidth: "400px" }}>
            <Input
              label="Email"
              type="email"
              value={confirmEmail}
              onChange={(e) => {
                setConfirmEmail(e.target.value);
                setConfirmEmailError("");
              }}
              error={!!confirmEmailError}
              helperText={confirmEmailError}
            />
          </Box>

          <Button
            variant="primary"
            disabled={confirmSubmitting}
            onClick={handleConfirmEmailSubmit}
            sx={{ mt: 3, minWidth: "200px" }}
          >
            {confirmSubmitting ? "Signing in..." : "Confirm and sign in"}
          </Button>
        </Box>
      </Container>
    );
  }

  if (status === "error") {
    return (
      <Container>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            mx: "auto",
          }}
        >
          {/* Error Icon */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              backgroundColor: "#FFF2F0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"
                fill="#D32F2F"
              />
            </svg>
          </Box>

          <Text variant="pageHeader" sx={{ mb: 2 }}>
            Action Failed
          </Text>

          <Text variant="paragraph" sx={{ color: "#666666", mb: 4 }}>
            {message}
          </Text>

          <Button
            variant="primary"
            onClick={() => navigate(ROUTES.SIGNIN, { replace: true })}
          >
            Back to Sign In
          </Button>
        </Box>
      </Container>
    );
  }

  if (status === "success") {
    return (
      <Container>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            mx: "auto",
          }}
        >
          {/* Success Icon */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              backgroundColor: "#E6F7FF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z"
                fill="#1890FF"
              />
            </svg>
          </Box>

          <Text variant="pageHeader" sx={{ mb: 2 }}>
            Success!
          </Text>

          <Text variant="paragraph" sx={{ color: "#666666", mb: 4 }}>
            {message}
          </Text>

          <Button
            variant="primary"
            onClick={() => navigate(ROUTES.SIGNIN, { replace: true })}
          >
            Continue to Sign In
          </Button>
        </Box>
      </Container>
    );
  }

  return null;
};

export default AuthAction;
